/*
  <fable-carousel>: dots (and optional arrows) for a native scroll-snap track.

  Markup:
    <fable-carousel class="fable-carousel" data-dot-label="Slide">
      <div class="fable-carousel__track" tabindex="0" role="region" aria-label="…">
        <div class="fable-carousel__slide">…</div>
      </div>
      <div class="fable-dots fable-carousel__dots" hidden></div>
      <button type="button" class="fable-carousel__prev" aria-label="…" hidden>…</button>   (optional)
      <button type="button" class="fable-carousel__next" aria-label="…" hidden>…</button>   (optional)
    </fable-carousel>

  The track scrolls natively (touch, wheel, keyboard when focused). This element only mirrors the
  scroll position into the dots and arrows. When the track does not overflow (for example a row
  that becomes a static flex layout on desktop) the controls stay hidden and nothing is scrolled.
  No autoplay.

  A scroller that keeps its own class names (the product gallery) names its track and slides with
  data-track and data-slide (selectors) instead of taking the two carousel classes.
*/
if (!customElements.get('fable-carousel')) {
  class FableCarousel extends HTMLElement {
    constructor() {
      super();
      this.pages = [];
      this.current = -1;
      this.frame = null;
      this.onScroll = this.onScroll.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.refresh = this.refresh.bind(this);
    }

    connectedCallback() {
      this.slideSelector = this.dataset.slide || '.fable-carousel__slide';
      this.track = this.querySelector(this.dataset.track || '.fable-carousel__track');
      if (!this.track) return;
      this.dots = this.querySelector('.fable-carousel__dots');
      this.prev = this.querySelector('.fable-carousel__prev');
      this.next = this.querySelector('.fable-carousel__next');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('click', this.onClick);
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      /* Slides are observed too: an image that loads late changes the snap offsets without resizing the track. */
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.refresh);
        this.resizeObserver.observe(this.track);
        Array.from(this.track.children).forEach((slide) => this.resizeObserver.observe(slide));
      } else {
        window.addEventListener('resize', this.refresh);
      }

      this.refresh();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.refresh);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    get slides() {
      return Array.from(this.track.children).filter((el) => el.matches(this.slideSelector) && el.offsetParent !== null);
    }

    get maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    /* scrollLeft that brings a slide to its snap position, clamped to the scrollable range.
       Right-to-left tracks scroll from 0 down to -maxScroll and snap on the right edge. */
    offsetFor(slide) {
      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const style = getComputedStyle(this.track);
      const rtl = style.direction === 'rtl';
      let offset;
      if (this.dataset.align === 'center') {
        offset = slideRect.left + slideRect.width / 2 - (trackRect.left + trackRect.width / 2);
      } else if (rtl) {
        offset = slideRect.right - trackRect.right + (parseFloat(style.scrollPaddingRight) || 0);
      } else {
        offset = slideRect.left - trackRect.left - (parseFloat(style.scrollPaddingLeft) || 0);
      }
      offset = Math.round(offset + this.track.scrollLeft);
      return rtl ? Math.max(Math.min(0, offset), -this.maxScroll) : Math.min(Math.max(0, offset), this.maxScroll);
    }

    /* One page per distinct reachable snap offset. A track that does not overflow has one page. */
    measure() {
      const pages = [];
      if (this.maxScroll > 1) {
        this.slides.forEach((slide) => {
          const offset = this.offsetFor(slide);
          const last = pages[pages.length - 1];
          if (!last || Math.abs(last.offset - offset) > 1) pages.push({ offset, slide });
        });
      }
      return pages;
    }

    refresh() {
      const pages = this.measure();
      const changed = pages.length !== this.pages.length;
      this.pages = pages;
      if (changed) this.buildDots();
      const scrollable = pages.length > 1;
      if (this.dots) this.dots.hidden = !scrollable;
      if (this.prev) this.prev.hidden = !scrollable;
      if (this.next) this.next.hidden = !scrollable;
      this.current = -1;
      this.update();
    }

    buildDots() {
      if (!this.dots) return;
      const label = this.dataset.dotLabel || 'Slide';
      this.dots.textContent = '';
      this.pages.forEach((page, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.dataset.index = String(index);
        dot.setAttribute('aria-label', `${label} ${index + 1}`);
        dot.setAttribute('aria-current', 'false');
        this.dots.appendChild(dot);
      });
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      if (this.pages.length < 2) return;
      const position = this.track.scrollLeft;
      let nearest = 0;
      this.pages.forEach((page, index) => {
        if (Math.abs(page.offset - position) < Math.abs(this.pages[nearest].offset - position)) nearest = index;
      });
      if (this.prev) this.prev.disabled = Math.abs(position) <= 1;
      if (this.next) this.next.disabled = Math.abs(position) >= this.maxScroll - 1;
      if (nearest === this.current) return;
      this.current = nearest;
      if (this.dots) {
        Array.from(this.dots.children).forEach((dot, index) => {
          dot.setAttribute('aria-current', index === nearest ? 'true' : 'false');
        });
      }
      this.dispatchEvent(new CustomEvent('fable-carousel:change', { bubbles: true, detail: { index: nearest } }));
    }

    goTo(index, instant) {
      const page = this.pages[Math.min(Math.max(0, index), this.pages.length - 1)];
      if (!page) return;
      this.track.scrollTo({
        left: page.offset,
        behavior: instant || this.reducedMotion.matches ? 'instant' : 'smooth',
      });
    }

    onClick(event) {
      const dot = event.target.closest('.fable-carousel__dots button');
      if (dot && this.dots && this.dots.contains(dot)) {
        this.goTo(Number(dot.dataset.index));
        return;
      }
      const arrow = event.target.closest('.fable-carousel__prev, .fable-carousel__next');
      if (arrow && this.contains(arrow) && !arrow.disabled) {
        const current = Math.max(0, this.current);
        this.goTo(arrow.classList.contains('fable-carousel__prev') ? current - 1 : current + 1);
      }
    }

    /* Theme editor: bring the selected block's slide into view. */
    onBlockSelect(event) {
      const slide = event.target.closest(this.slideSelector);
      if (!slide || !this.track.contains(slide) || this.maxScroll <= 1) return;
      this.track.scrollTo({ left: this.offsetFor(slide), behavior: 'instant' });
    }
  }

  customElements.define('fable-carousel', FableCarousel);
}
